import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';

import Typography from 'carbon-react/lib/components/typography';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ModifyWorkspaceView } from '@futrli/views/views/workspaces/ModifyWorkspaceView/ModifyWorkspaceView';
import { useApiView } from '@futrli/views/hooks/useApiView';

interface DeleteConsolidationDialogProps {
  consolidationId: string;
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
}

export const DeleteConsolidationDialog = ({
  consolidationId,
  isOpen,
  onClose,
  workspaceId
}: DeleteConsolidationDialogProps) => {
  const flags = useFlags();
  const intl = useIntl();

  const view = useApiView(useMemo(() => new ModifyWorkspaceView(flags, workspaceId), [flags, workspaceId]));

  const { screen, handleAction, handleClose } = useDialogWizardState({
    error: view.state.errorUpdating,
    isLoading: view.state.isLoading || view.state.isDeletingConsolidation,
    onAction: () => view.deleteConsolidation(consolidationId),
    onClose
  });

  return (
    <DeleteDialogWizard
      cancelButtonText={intl.formatMessage({
        id: 'common.cancel',
        defaultMessage: 'Cancel'
      })}
      dataTestId="delete-consolidation-dialog"
      deleteButtonText={intl.formatMessage({
        id: 'consolidation.delete_consolidation.confirmation_button',
        defaultMessage: 'I am sure, delete this consolidation'
      })}
      deleteDescription={intl.formatMessage(
        {
          id: 'consolidation.delete_consolidation.description',
          defaultMessage:
            'Deleting a consolidation is irreversible.<br></br><br></br>It will <highlight>delete</highlight> all reports and budgets or forecasts created.'
        },
        {
          br: () => <br />,
          highlight: chunks => (
            <Typography color="errorRed" fontSize="14px" fontWeight="500" mb={0} variant="span">
              {chunks}
            </Typography>
          )
        }
      )}
      errorMessage={view.state.errorUpdating?.message}
      isOpen={isOpen}
      loadingMessage={
        view.state.isLoading
          ? intl.formatMessage({
              id: 'common.loading_please_wait',
              defaultMessage: 'Loading, please wait...'
            })
          : intl.formatMessage({
              id: 'consolidation.delete_consolidation.deleting',
              defaultMessage: 'Deleting consolidation...'
            })
      }
      onClose={handleClose}
      onDelete={handleAction}
      screen={screen}
      successMessage={intl.formatMessage({
        id: 'consolidation.delete_consolidation.deleted',
        defaultMessage: 'Consolidation deleted'
      })}
      title={intl.formatMessage({
        id: 'common.warning',
        defaultMessage: 'Warning!'
      })}
    />
  );
};
